// Copyright (c) Hamilton Manufacturing Corp.
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import useDebounce from '../api/debounce';
import { useGlobalState } from '../globalState/GlobalStateProvider';
import { FormContext } from './Form';
import Icon from './Icon';
import { mobileView } from '../api/utility';

const Wrapper = styled.div`
    margin: 0.4em 0.5em 0.4em 0.4em;
    border-radius: 0.75rem;
    box-shadow: ${({ focused, theme }) => focused ? `0px 0px 2px 1px ${theme.input.active}` : 'unset'};
    transition: box-shadow 400ms;
`;
const InputWrapper = styled.div`
    display: flex;
    align-items: center;
    border-radius: 0.75rem;
    padding: 1em 0.5em 0.15em 0.5em;
    color: inherit;
    transition: background-color 400ms;

    background-color: ${({ theme }) => theme.input.background};
    border: 1px solid ${({ value, theme }) => value ? theme.input.disabled : `${theme.input.disabled}40`};
    
    ${({ touched, theme }) => touched && `
        border: 1px solid ${touched === true ? theme.error : theme.input.disabled};
    `}

    ${({ disabled, theme }) =>
        disabled && `
        color: ${theme.input.disabledText};
        background-color: ${theme.input.disabled};
        cursor: default;
    `}
`;
const InputCSS = styled.input`
    color: inherit;
    appearance: none;
    background-color: transparent;
    border: none;
    width: 100%;
    font-family: inherit;
    font-size: inherit;
    cursor: inherit;
    line-height: inherit;
    z-index: 1;
    outline: none;
    font-size: 0.9em;
    
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    &::placeholder {
        color: ${({ theme }) => theme.input.disabled}40;
    }

    &:disabled {
        opacity: 1;
    }
`;
const Row = styled.div`
    color: ${({ theme, isdisabled }) => isdisabled ? theme.input.disabledText : theme.input.text};
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: center;
    position: relative;
    flex-grow: 1;
`;
const Tag = styled.sup`
    position: absolute;
    font-size: 0.5em;
    margin: 0 0 0em 1em;
`;
const Error = styled(Tag)`
    color: ${({ theme }) => theme.error};
`;

const MinContent = styled.div`
    position: absolute;
    margin-left: 2rem;
    transition: all 0.2s ease-out;
    color: ${({ theme, isdisabled }) => isdisabled ? theme.input.disabledText : theme.input.text};

    ${({ hasValue, mobile }) => hasValue && `
        transform: translate(0%, -100%);
        font-size: 0.5em;

        ${mobile && `
            margin-left: 1.5rem;
            animation: fadeInSlow;
            animation-duration: 0.5s;
            animation-fill-mode: forwards;
        `}
    `}
`;

const executeScroll = (ref) => { } //ref.current.scrollIntoView({ inline: "center" })

const Input = ({ ...props }) => {
    const { state: {
        // mobileView
    } } = useGlobalState();

    const inputRef = useRef();
    const [hasValue, setHasValue] = useState(props.value || false);
    const [focused, setFocused] = useState(false);

    return (
        <Row onClick={() => inputRef.current.focus()}>
            {props.standalone ?
                <>
                    <MinContent hasValue={hasValue !== null || undefined || ''} mobile={mobileView} isdisabled={props.disabled}>
                        {props.label}
                    </MinContent>
                    <Wrapper focused={focused}>
                        <InputWrapper disabled={props.disabled}>
                            <InputCSS
                                ref={inputRef}
                                pattern={props.numpad ? '\\d*' : props.pattern}
                                onFocus={() => {
                                    setFocused(true)
                                    executeScroll(inputRef)
                                }}
                                onBlur={() => setFocused(false)}
                                {...props}
                            />
                        </InputWrapper>
                    </Wrapper>
                </> :
                <FormInput isFocused={setFocused} inputRef={inputRef} focused={focused} {...props} />}
        </Row>
    );
}

const FormInput = ({ name, isFocused, inputRef, focused, ...props }) => {
    const { state: {
        // mobileView
    } } = useGlobalState();

    const { values, submitting, editing, errors, setValues, setErrors } = useContext(FormContext);
    const [invalid, setInvalid] = useState(false);
    const [showPW, setShowPW] = useState(false);
    const [disable, setDisable] = useState(false);
    const [firstRun, setFirstRun] = useState(true);
    const [invalidMessage, setInvalidMessage] = useState('');

    const [hasValue, setHasValue] = useState(props.value || false);

    useLayoutEffect(() => setHasValue(values[name] ? true : false), []);

    // useEffect(() => {
    //     if (firstRun) {
    //         setFirstRun(false)
    //         return;
    //     }

    //     // if (!props.disabledcondition) return;
    //     // setDisable(values[props.disabledcondition]);
    //     // if (values[props.disabledcondition]) {
    //     //     setInvalidation(false)
    //     // } else {
    //     //     // validate(values[name])
    //     // }
    // }, [values])

    useEffect(() => {
        if (firstRun) {
            setFirstRun(false)
            return;
        }

        setDisable(values[props.disabledcondition]);

        if (!props.disabledcondition) return;
        if (values[props.disabledcondition]) {
            setInvalidation(false)
        } else {
            validate(values[name])
        }
    }, [values[props.disabledcondition]])

    useEffect(() => {
        if (!values[name] && document.activeElement === inputRef.current) {
            setHasValue(true)
        } else {
            setHasValue(values[name] ? true : false)
        }
    }, [values[name]])

    useEffect(() => {
        if (!props.required && errors[name] === 'unTouched') {
            if (errors[name] === false) return;
            setErrors({
                ...errors,
                [name]: false
            })
        }

        if (!props.confirmation) return;
        if (errors[name] === (values[name] !== values[props.confirmation])) return;
        setErrors({
            ...errors,
            [name]: values[name] !== values[props.confirmation]
        });
    }, [errors, setErrors]);

    const debouncedValue = useDebounce(values[name], props.debounce)
    const firstUpdate = useRef(true);
    useEffect(() => {
        if (!firstUpdate.current) {
            validate(debouncedValue);
        }
        firstUpdate.current = false;
    }, [debouncedValue])

    const handleInput = (e) => {
        e = e.target.value;
        if (props.mask) {
            e = e.replace(props.mask.replace, '')
        }
        const val = props.sanitize ? typeof props.sanitize === 'function' ? props.sanitize(e) : e.replace(props.sanitize, '') : e;
        setValues(v => {
            return {
                ...v,
                [name]: val
            }
        })
    }

    const validate = async val => {
        let invalid = false;

        if (val === '' && props.required) {
            setInvalidMessage('Input is required.')
            invalid = true;
        }

        if (props.validate && val !== '') {
            if (Array.isArray(props.validate)) {
                props.validate.forEach(e => {
                    if (!invalid) {
                        invalid = !(val.defaultMatch(e.regex) !== '');
                        if (invalid) setInvalidMessage(e.message);
                    }
                })
            } else if (typeof props.validate === 'function') {
                invalid = await new Promise((resolve) => props.validate(val, setInvalidMessage, resolve))
            } else {
                invalid = !(val.defaultMatch(props.validate) === val);
            }
        };

        if (props.confirmation && val !== values[props.confirmation]) {
            invalid = true;
        }
        setInvalidation(invalid)
    }

    const setInvalidation = (invalid) => {
        setInvalid(invalid);
        setErrors(v => {
            return {
                ...v,
                [name]: invalid
            }
        });
    }

    const maintainCursor = e => {
        if (!props.mask) return;
        const caret = e.target.selectionStart
        const element = e.target
        if (caret === e.target.value.length) return;
        window.requestAnimationFrame(() => {
            element.selectionStart = element.selectionEnd = caret
        })
    }

    // eslint-disable-next-line no-extend-native
    String.prototype.defaultMatch = function (match, defaultVal = '') {
        return (this.match(match) || [defaultVal])[0]
    }

    return (
        <>
            <MinContent hasValue={hasValue !== null || undefined || ''} mobile={mobileView} isdisabled={props.disabled || submitting || !editing || disable}>
                {props.label} {!props.required ? ' - Optional' : null}
            </MinContent>
            <Wrapper focused={focused}>
                <InputWrapper disabled={props.disabled || submitting || !editing || disable} touched={errors[name]}>
                    <InputCSS
                        value={maskInput(values[name], props.mask)}
                        ref={inputRef}
                        pattern={props.numpad ? '\\d*' : props.pattern}
                        onInput={handleInput}
                        onChange={maintainCursor}
                        onFocus={() => {
                            setHasValue(true)
                            isFocused(true)
                            executeScroll(inputRef)
                        }}
                        disabled={props.disabled || submitting || !editing || disable}
                        onBlur={(e) => {
                            setHasValue(values[name] ? true : false)
                            handleInput(e)
                            isFocused(false)
                        }}
                        {...props}
                        type={(props.type === 'password' && showPW) ? 'text' : props.type}
                        label={props.type}
                    />
                    {
                        props.type === 'password' ?
                            <Icon
                                style={{ width: '1em' }}
                                icon={showPW ? 'crossEye' : 'eye'}
                                onClick={() => setShowPW(!showPW)}
                            /> : null
                    }
                </InputWrapper>
                {props.tag ? <Tag>{props.tag}</Tag> : null}
                {invalid ? <Error>{invalidMessage || props.error}</Error> : null}
            </Wrapper>
        </>
    );
}

export default Input;

const maskInput = (val, mask) => {
    if (!mask || !val) return val;

    if (typeof mask === 'function') {
        return mask(val)
    }

    mask = mask.format;

    if (Array.isArray(mask)) {
        mask.forEach(e => val = val.replace(e.selector, e.replacement))
    } else {
        val = val.replace(mask.selector, mask.replacement)
    }
    return val;
}